import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Globe2, ClipboardCheck, Users, Factory, ShieldCheck } from "lucide-react";
import Seo, { ORG_LD } from "@/components/site/Seo";
import { RevealLines } from "@/components/site/RevealText";

const SERVICES = [
  {
    icon: Globe2,
    title: "Global Sourcing",
    body: "Access to a qualified supplier network across best-cost manufacturing regions — India, China, Southeast Asia, the Middle East, Europe and North America.",
    points: ["Requirement-first, geography-second", "Supplier discovery from specs, drawings or part numbers", "Best-cost matched to technical fit"],
  },
  {
    icon: ClipboardCheck,
    title: "Procurement Support",
    body: "End-to-end procurement help — technical clarification, negotiation, PO placement, and delivery follow-through — for buyers with lean teams.",
    points: ["Specification and drawing review", "Negotiation, PO placement, and payment terms", "Delivery, logistics, and documentation follow-through"],
  },
  {
    icon: Users,
    title: "Supplier Identification",
    body: "We identify, evaluate and qualify manufacturers against your specific technical, quality and commercial requirements — before you engage.",
    points: ["Long-list from global manufacturing regions", "Technical capability and capacity screen", "Commercial and reliability shortlist"],
  },
  {
    icon: Factory,
    title: "Custom Manufacturing",
    body: "Products manufactured to your drawings, specifications, samples or technical requirements — across CNC, sheet metal, forging, casting, moulding and heavy fabrication.",
    points: ["DFM feedback from partner factories", "Prototype to serial production", "First-article inspection and PPAP where required"],
  },
  {
    icon: ShieldCheck,
    title: "Quality & Supplier Verification",
    body: "Every partner we present is qualified for technical capability, quality systems, capacity, reliability and export experience — not just price.",
    points: ["Factory qualification questionnaires", "Third-party inspection coordination (BV / SGS / TÜV / DNV)", "Traceability and mill-cert (EN 10204 3.1/3.2) support"],
  },
];


export default function Capabilities() {
  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-capabilities">
      <Seo
        title="Capabilities · Global Sourcing, Procurement & Supplier Verification"
        description="Five services from one accountable partner — Global Sourcing, Procurement Support, Supplier Identification, Custom Manufacturing and Quality & Supplier Verification."
        path="/capabilities"
        structuredData={ORG_LD}
      />
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Capabilities</div>
        <RevealLines
          as="h1"
          lines={[<>Five services.</>, <>One accountable partner.</>]}
          className="font-cabinet font-black tracking-[-0.04em] leading-[0.92] text-[13vw] md:text-[7vw] lg:text-[5.4vw] text-nx-bg"
        />
        <p className="mt-8 max-w-2xl text-nx-bg/75 leading-relaxed text-lg">
          NEXORIQ is built around what industrial buyers actually need — from finding a supplier, to
          qualifying them, to getting the product delivered.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12"
              data-testid={`capability-${i}`}
            >
              <div className="lg:col-span-4">
                <div className="flex items-center gap-3 mb-6">
                  <s.icon size={22} className="text-nx-copper" />
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-copper">Service {String(i + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="font-cabinet font-black tracking-[-0.03em] leading-[0.95] text-3xl md:text-4xl text-nx-navy">
                  {s.title}
                </h2>
              </div>
              <div className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-nx-navy/20">
                <p className="text-nx-navy2 text-lg leading-relaxed max-w-2xl">{s.body}</p>
                <ul className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4 border-t border-nx-navy/20 pt-6">
                  {s.points.map((p) => (
                    <li key={p} className="text-nx-navy2 leading-relaxed">
                      <span className="text-nx-copper mr-2">→</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="bg-nx-copper text-nx-bg px-6 md:px-12 lg:px-20 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5.4vw] text-nx-bg">
              Difficult to source?<br />Let's talk.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/sourcing-request" className="inline-flex items-center gap-3 bg-nx-navydark text-nx-bg px-6 py-4 hover:bg-nx-bg hover:text-nx-navydark border border-nx-navydark transition-colors font-mono text-[0.72rem] uppercase tracking-[0.22em]" data-testid="capabilities-cta">
              Submit a Sourcing Requirement <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
